import React from "react";
import { ChatProvider } from "../../context/ChatContext";
import { createChatClient } from "../../lib/chatApi";
import { SearchInput } from "../../components/ChatInput";
import { X } from "lucide-react";
import {
  appendTestSessionId,
  getBackendBaseUrl,
  persistTestConfig,
  persistTestSessionId,
  updateGlobalTestConfig,
} from "../config";
import { useLocale } from "../../context/LocaleContext";

interface BrowserEntrypointProps {
  storeId: string;
  apiBase: string;
  onNavigateToBrowser?: (query?: string) => void;
  onClose?: () => void;
}

export function BrowserEntrypoint({ storeId, apiBase, onNavigateToBrowser, onClose }: BrowserEntrypointProps) {
  // Mirror SPA dev/prod selection if present (defaults to prod)
  const getBackendMode = React.useCallback((): 'dev' | 'prod' => {
    try {
      const q = new URL(window.location.href).searchParams.get('backend');
      if (q === 'dev' || q === 'prod') return q;
    } catch {}
    try {
      const saved = localStorage.getItem('ww/spa/backendMode');
      if (saved === 'dev' || saved === 'prod') return saved as 'dev' | 'prod';
    } catch {}
    return 'prod';
  }, []);

  const getOrigins = React.useCallback((mode: 'dev' | 'prod') => {
    const PROD = getBackendBaseUrl(); // Use configurable backend URL
    const DEV = 'https://wandway-back.onrender.com'; // Dev mode still uses hardcoded URL
    const origin = (mode === 'prod' ? PROD : DEV).replace(/\/$/, '');
    return {
      initUrl: `${origin}/widget/init`,
      chatBase: `${origin}/chat`,
    };
  }, []);

  const refreshToken = React.useCallback(async () => {
    try {
      const MODE = getBackendMode();
      const { initUrl, chatBase } = getOrigins(MODE);
      const url = `${initUrl}?store_id=${encodeURIComponent(storeId)}`;
      const urlWithSession = appendTestSessionId(url);
      const res = await fetch(urlWithSession, { 
        method: "GET", 
        credentials: "omit", 
        cache: "no-store",
        mode: "cors"
      });
      if (!res.ok) return null;
      const data = await res.json();
      persistTestSessionId(data.test_session_id);
      persistTestConfig(data.test_config);
      updateGlobalTestConfig({ testSessionId: data.test_session_id, testConfig: data.test_config });
      const base = (data.api_base || "").replace(/\/$/, "");
      const secureBase = base.replace(/^http:\/\//i, "https://");
      const fixedApiBase = /\/chat$/.test(secureBase) ? secureBase : `${secureBase}/chat`;
      const nextToken = data.token as string | undefined;
      if (!nextToken) return null;
      const nextBase = (MODE === 'prod') ? fixedApiBase : chatBase;
      const g = (window as any).__WW__ || {};
      (window as any).__WW__ = {
        ...g,
        storeId,
        apiBase: nextBase,
        themeCssUrl: g.themeCssUrl,
        flags: data.flags,
        ...(data.test_session_id ? { testSessionId: data.test_session_id } : {}),
        ...(Array.isArray(data.test_config) ? { testConfig: data.test_config } : {}),
      };
      return { token: nextToken, apiBase: nextBase };
    } catch {
      return null;
    }
  }, [storeId, getBackendMode, getOrigins]);

  const client = React.useMemo(() => createChatClient({ storeId, apiBase, refreshToken }), [storeId, apiBase, refreshToken]);

  return (
    <ChatProvider client={client}>
      <BrowserEntrypointInner onNavigateToBrowser={onNavigateToBrowser} onClose={onClose} />
    </ChatProvider>
  );
}

function BrowserEntrypointInner({ onNavigateToBrowser, onClose }: { onNavigateToBrowser?: (query?: string) => void; onClose?: () => void }) {
  const { t } = useLocale();
  const samples = React.useMemo(() => [
    "Varm skidjacka",
    "Nybörjarskidor för junior",
    "Alpinpjäxor lätta att ta på och av",
    "Goggles som klarar stark sol",
  ], []);

  const handleSubmit = async (value: string) => {
    const q = (value || '').trim();
    if (!q) return;
    onNavigateToBrowser?.(q);
  };

  return (
    <div
      className="ww-browser-entry h-full flex flex-col"
      style={{ backgroundColor: 'var(--color-background)' }}
    >
      {/* Top bar: close button */}
      {onClose && (
        <div className="flex items-center justify-end p-[12px]">
          <button
            type="button"
            className="ww-icon-btn w-[36px] h-[36px] flex items-center justify-center rounded-full border"
            aria-label={t("common.close")}
            onClick={onClose}
            title={t("common.close")}
          >
            <X className="w-[24px] h-[24px]" />
          </button>
        </div>
      )}
      <div className="flex-1 p-6 flex flex-col items-center justify-center">
        <div className="w-[90%] max-w-[760px] mx-auto">
        <h1 className="text-3xl md:text-4xl font-bold text-center mb-4" style={{ fontFamily: 'var(--font-heading)', color: 'var(--color-foreground)' }}>
          {t("search.free_title")}
        </h1>
        <p className="text-center mb-8" style={{ fontFamily: 'var(--font-body)', color: 'var(--text-tertiary)' }}>
          {t("search.free_subtitle")}
        </p>

        <SearchInput
          placeholder={t("search.placeholder")}
          selectedOptions={[]}
          onRemoveOption={() => {}}
          onSubmit={(text) => handleSubmit(text)}
        />

        <div className="flex flex-wrap gap-[8px] mt-[16px] justify-center">
          {samples.map((s) => (
            <button
              key={s}
              type="button"
              className="px-[12px] py-[4px] rounded-full border text-[14px]"
              onClick={() => handleSubmit(s)}
              aria-label={`Exempel: ${s}`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>
      </div>
    </div>
  );
}


